<template>
  <div class="content">
    <h1>{{ pageTitle }}</h1>
    <div class="content-body">
      <div class="table-wrapper block">
        <AssembledPartCostInfo v-if="costEstimator" :assembled-part-cost="partCost" :loading="loading"></AssembledPartCostInfo>
        <div class="tabs">
          <Tabs v-model="selectedTab" name="outTab">
            <TabPane label="Parts" name="parts" tab="outTab">
              <div id="pie-chart" v-if="chartsData.length > 0">
                <div class="soft-hard-cost">
                  <p>{{ $t('cost.costed_bom.soft_hard_cost') }}</p>
                <CostAnalysisChart :data="chartsData" :name-field="softHardFilters"></CostAnalysisChart>
                </div>
                <div class="total-cost-components">
                  <p>{{ $t('cost.costed_bom.total_cost_components') }}</p>
                  <CostAnalysisChart :data="chartsData" :name-field="totalCostFilters"></CostAnalysisChart>
                </div>
                <div class="recurring-cost">
                  <p>{{ $t('cost.costed_bom.recurring') }}</p>
                  <CostAnalysisChart :data="chartsData" :name-field="recurringFilters"></CostAnalysisChart>
                </div>
                <div class="total" v-if="costEstimator">
                  <p class="flex">
                    <span class="title">{{ $t('cost.materials.net_cost') }}:</span>
                    <span>{{ intlCurrencyNumber(costEstimator.root_assembly_part_cost.currency, net_cost, 2, 2) }}</span>
                  </p>
                  <p class="flex">
                    <span class="title">{{ $t('cost.materials.quality_cost') }}:</span>
                    <span>{{ intlCurrencyNumber(costEstimator.root_assembly_part_cost.currency, quality_cost, 2, 2) }}</span>
                  </p>
                  <p class="flex"><span class="title">{{ $t('cost.materials.total') }}:</span> <span>{{ intlCurrencyNumber(costEstimator.root_assembly_part_cost.currency, total, 2, 2) }}</span></p>
                  <p class="flex">
                    <span class="title">{{ $t('cost.materials.total_rate') }}:</span>
                    <span>{{ intlCurrencyNumber(costEstimator.root_assembly_part_cost.currency, total_tax, 2, 2) }}</span>
                  </p>
                </div>
              </div>
              <PartsLineItems v-if="selectedTab === 'parts' && costEstimator" :part-cost="partCost" :is-children="true"></PartsLineItems>
            </TabPane>
            <TabPane v-for="(item, index) in domains" :label="item.label" :name="item.key" :key="index" tab="outTab">
              <CostEstimateSubmitsWidget  v-if="partCost && partCost.id"
                                         :part-cost-id="partCost.id"
                                         :data="{ name: item.key + '.activities'}"
                                         class="inline">
              </CostEstimateSubmitsWidget>
              <ActivityLineItems :part-cost="partCost" :domain="item.key" v-if="selectedTab === item.key"></ActivityLineItems>
            </TabPane>
          </Tabs>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Domains from './data/domains'
import { mapGetters, mapActions } from 'vuex'
import {
  AssembledPartCostInfo,
  ActivityLineItems,
  PartsLineItems,
  CostEstimateSubmitsWidget } from './components'
import CostAnalysisChart from './components/charts/cost-analysis-chart'
import { intlCurrencyNumber } from '@/libs/util'

export default {
  name: 'CostEstimator',
  components: {
    AssembledPartCostInfo,
    ActivityLineItems,
    PartsLineItems,
    CostEstimateSubmitsWidget,
    CostAnalysisChart
  },
  data () {
    return {
      loading: false,
      selectedTab: 'parts',
      domains: Domains,
      costEstimator: null,
      chartsData: [],
      net_cost: 0,
      quality_cost: 0,
      total: 0,
      total_tax: 0,
      softHardFilters: [
        { report_name: "backend_hard_cost" },
        { report_name: "backend_soft_cost" }
      ],
      totalCostFilters: [
        { report_name: "backend_scrap_cost" },
        { report_name: "backend_engineering_cost" },
        { report_name: "backend_production_cost" },
        { report_name: "express_cost" }
      ],
      recurringFilters: [
        { report_name: "recurring_cost" },
        { report_name: "non_recurring_cost" }
      ]
    }
  },
  computed: {
    ...mapGetters({
      partCost: 'currentPartCost',
      partCostLoaded: 'hasPartCostLoaded'
    }),
    pageTitle () {
      document.title = this.$t('menu.estimators.title') + ' - ' + this.partCost?.manufacturer_part.display_name
      return document.title
    }
  },
  mounted () {
    if (this.$route.params.costId) {
      this.loadAssembledPartCostById(this.$route.params.costId)
    }
  },
  watch: {
    '$route.params.costId': function (value) {
      if (value) {
        this.loadAssembledPartCostById(value)
      }
    },
    partCost: {
      handler(newVal) {
        if (newVal) {
          this.loadCostEstimator(this.partCost.cost_estimator_id)
          this.loadChartsData(this.partCost.cost_estimator_id)
        }
      },
      deep: true
    }
  },
  methods: {
    ...mapActions([
      'loadAssembledPartCostById',
      'getCostEstimatorInfo',
      'getCostedBOMReport'
    ]),
    intlCurrencyNumber,
    loadCostEstimator(id) {
      this.getCostEstimatorInfo(id).then(res => {
        this.costEstimator = res.data
      })
    },
    loadChartsData(id) {
      let reports = [
        'backend_hard_cost',
        'backend_soft_cost',
        'backend_scrap_cost',
        'backend_engineering_cost',
        'backend_production_cost',
        'backend_net_cost',
        'backend_total_tax',
        'backend_total_cost',
        'express_cost',
        'recurring_cost',
        'non_recurring_cost'
      ]
      this.getCostedBOMReport({ costEstimatorId: id, reports, assembled_part_cost_id: this.$route.params.costId }).then(res => {
        this.net_cost = _.find(res.data.reports, { report_name: "backend_net_cost" }).total_cost
        this.quality_cost = _.find(res.data.reports, { report_name: "backend_scrap_cost" }).total_cost
        this.total = _.find(res.data.reports, { report_name: "backend_total_cost" }).total_cost
        this.total_tax = _.find(res.data.reports, { report_name: "backend_total_tax" }).total_cost
        this.chartsData = res.data.reports
      })
    }
  }
}
</script>
<style lang="less" scoped>
.currency-select-widget {
  float: right;
  height: 32px;
  line-height: 32px;
  padding: 0 32px;
}
.submit-cost-estimates-btn {
  float: right;
}
#pie-chart {
    display: flex;
    align-items: center;
    .soft-hard-cost, .total-cost-components, .recurring-cost {
      text-align: center;
    }
    .total {
      margin-top: 100px;
      margin-left: 0;
      width: 200px;
      .flex {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      .title {
        font-size: 16px;
        font-weight: bold;
      }
    }
  }
</style>
